import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { databaseService } from '@/services/databaseService';
import { syncAll } from '@/services/syncService';
import { APP_CONSTANTS } from '@/config/constants';
import { getPartnerOwnerState, partnerOwnerIdentityMatches, type PartnerOwnerScope, type PartnerOwnerState } from '@/services/partnerChecklistOwnerService';
import { readPartnerChecklistBaseline, savePartnerChecklistBaseline } from '@/services/checklist/partnerChecklistBaseline';

export async function hasPartnerChecklistPending(checklistId: string): Promise<boolean> {
  const [operations, images] = await Promise.all([
    databaseService.getPendingOperations(),
    databaseService.listItems<{ item_id: string }>('pending_images'),
  ]);
  return operations.some(op => op.itemId === checklistId) || images.some(image => image.item_id === checklistId);
}

export function usePartnerChecklist(input: PartnerOwnerScope & {
  isOnline: boolean;
  isFocused: boolean;
  editing: boolean;
  onRefetch: () => Promise<unknown> | void;
}) {
  const scopeKey = JSON.stringify([input.companyId, input.userId, input.checklistId]);
  const latest = useRef(input);
  latest.current = input;
  const [snapshot, setSnapshot] = useState<{ key: string; state: PartnerOwnerState } | null>(null);
  const [blocked, setBlocked] = useState(false);
  const busy = useRef<number | null>(null);
  const generation = useRef(0);
  const observedScope = useRef<string | null>(null);
  const appActive = useRef(Platform.OS === 'web' || (AppState.currentState !== 'background' && AppState.currentState !== 'inactive'));
  const pollingRequired = useRef(true);
  const isVisible = () => appActive.current &&
    (Platform.OS !== 'web' || typeof document === 'undefined' || document.visibilityState !== 'hidden');
  const refresh = useCallback(async () => {
    const scope = latest.current;
    if (!scope.isOnline || !scope.isFocused || !scope.checklistId || !partnerOwnerIdentityMatches(scope) || busy.current === generation.current || !isVisible()) return;
    const startedGeneration = generation.current;
    const sameScope = () => generation.current === startedGeneration &&
      latest.current.isFocused && isVisible() && partnerOwnerIdentityMatches(scope) && latest.current.checklistId === scope.checklistId;
    busy.current = startedGeneration;
    try {
      const state = await getPartnerOwnerState(scope);
      if (!sameScope() ||
          state.empresa.id !== scope.companyId || state.checklist.id !== scope.checklistId) return;
      pollingRequired.current = state.enabled || state.touched || state.active;
      setSnapshot({ key: JSON.stringify([scope.companyId, scope.userId, scope.checklistId]), state });
      if (state.active) setBlocked(true);
      const previous = readPartnerChecklistBaseline(scope.companyId, scope.userId, scope.checklistId);
      // Não substituir o rascunho nem renovar sua revisão enquanto houver trabalho pendente.
      if (latest.current.editing || await hasPartnerChecklistPending(scope.checklistId)) {
        if (sameScope()) {
          setBlocked(state.active || (state.touched && (!previous || state.revision !== previous.revision)));
        }
        return;
      }
      if (!sameScope() || latest.current.editing) return;
      if (state.touched && (!previous || previous.revision !== state.revision || previous.active !== state.active)) {
        setBlocked(true);
        // O pull existente já preserva a fila e as capturas locais; não criar um segundo caminho de escrita.
        await syncAll();
        if (!sameScope() || latest.current.editing ||
            await hasPartnerChecklistPending(scope.checklistId)) return;
        const local = await databaseService.listItems<Record<string, unknown>>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME, { checklist_id: scope.checklistId },
        );
        const localChecklist = await databaseService.readItem<{ status: string }>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, scope.checklistId,
        );
        if (localChecklist?.status !== state.checklist.status || state.value_rows.some(remote => {
          const row = local.find(item => item.id === remote.id && item.empresa_id === scope.companyId);
          return !row || row.valor !== remote.valor || (row.observacao ?? null) !== (remote.observacao ?? null);
        })) return;
        if (!sameScope() || latest.current.editing) return;
        await latest.current.onRefetch();
      }
      if (!sameScope() || latest.current.editing) return;
      savePartnerChecklistBaseline(scope.companyId, scope.userId, scope.checklistId, state);
      setBlocked(state.active);
    } catch {
      // Empresas sem o recurso mantêm o fluxo anterior; revisão já conhecida não é renovada em falha.
      const previous = readPartnerChecklistBaseline(scope.companyId, scope.userId, scope.checklistId);
      if (sameScope()) {
        setBlocked(wasBlocked => wasBlocked || previous?.active === true);
      }
    } finally { if (busy.current === startedGeneration) busy.current = null; }
  }, []);
  useEffect(() => {
    generation.current += 1;
    if (observedScope.current !== scopeKey) {
      observedScope.current = scopeKey;
      pollingRequired.current = true;
      setSnapshot(null);
      const previous = readPartnerChecklistBaseline(input.companyId, input.userId, input.checklistId);
      setBlocked(previous?.active === true);
    }
    if (!input.isFocused) return;
    appActive.current = Platform.OS === 'web' || (AppState.currentState !== 'background' && AppState.currentState !== 'inactive');
    void refresh();
    const timer = setInterval(() => { if (pollingRequired.current) void refresh(); }, 10000);
    const appState = Platform.OS === 'web' ? null : AppState.addEventListener('change', value => {
      appActive.current = value === 'active';
      if (appActive.current) void refresh();
    });
    const resume = () => { if (isVisible()) void refresh(); };
    if (Platform.OS === 'web' && typeof document !== 'undefined') document.addEventListener('visibilitychange', resume);
    return () => {
      generation.current += 1; clearInterval(timer); appState?.remove();
      if (Platform.OS === 'web' && typeof document !== 'undefined') document.removeEventListener('visibilitychange', resume);
    };
  }, [scopeKey, input.isOnline, input.isFocused, refresh]);
  return { state: snapshot?.key === scopeKey ? snapshot.state : null, blocked, refresh };
}
